import { type FC, useEffect, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import { EquipmentSelector } from './EquipmentSelector'

// Simplified interface for the modal - only includes fields we actually need
interface EquipmentData {
  id: string
  brand: string
  name: string
  typeName: string
}

interface EditEquipmentModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (equipment: EquipmentData) => void
  currentEquipment?: {
    id: string
    brand: string
    name: string
    typeName: string
  }
  isOverrideMode?: boolean
  inheritedFrom?: string
}

export const EditEquipmentModal: FC<EditEquipmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  currentEquipment,
  isOverrideMode = false,
  inheritedFrom,
}) => {
  const [submitting, setSubmitting] = useState(false)
  const [currentSelection, setCurrentSelection] = useState<{
    category: string
    model: string
    equipmentData?: EquipmentData
  }>({
    category: '',
    model: '',
  })

  // Initialize with current equipment data
  useEffect(() => {
    if (currentEquipment) {
      setCurrentSelection({
        category: currentEquipment.typeName,
        model: currentEquipment.id,
        equipmentData: {
          id: currentEquipment.id,
          brand: currentEquipment.brand,
          name: currentEquipment.name,
          typeName: currentEquipment.typeName,
        },
      })
    }
  }, [currentEquipment])

  const handleSubmit = async () => {
    if (!currentSelection.equipmentData) {
      return
    }

    setSubmitting(true)
    try {
      const equipment: EquipmentData = {
        id: currentSelection.equipmentData.id,
        brand: currentSelection.equipmentData.brand,
        name: currentSelection.equipmentData.name,
        typeName: currentSelection.equipmentData.typeName,
      }

      await Promise.resolve(onSubmit(equipment))
    } finally {
      setSubmitting(false)
    }
  }

  const handleSelectionChange = (
    category: string,
    model: string,
    equipmentData?: EquipmentData
  ) => {
    setCurrentSelection({ category, model, equipmentData })
  }

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        isOverrideMode
          ? i18n.t('job_catalog.equipment.modal.override_title')
          : i18n.t('job_catalog.equipment.modal.edit_title')
      }
      primaryAction={{
        label: isOverrideMode
          ? i18n.t('job_catalog.equipment.actions.override')
          : i18n.t('common.update'),
        onClick: handleSubmit,
        loading: submitting,
        disabled: !currentSelection.equipmentData,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      {isOverrideMode && inheritedFrom && (
        <Box paddingBottom="s16">
          <Text size="400" color="grey600">
            {i18n.t('job_catalog.equipment.modal.override_description', {
              source: inheritedFrom,
              category: currentEquipment?.typeName,
            })}
          </Text>
        </Box>
      )}
      <EquipmentSelector
        onAdd={() => {}} // Not used in edit mode
        selectedEquipment={[]} // Not used in edit mode
        onSelectionChange={handleSelectionChange}
        initialSelectedCategory={currentSelection.category}
        initialSelectedModel={currentSelection.model}
        restrictToCategory={isOverrideMode ? currentEquipment?.typeName : undefined}
      />
    </F0Dialog>
  )
}
